import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { scopePartiesToBallot } from '../../../app/election-parameters/ballot/ballot-helpers';
import { getUserData, type ResultType } from '../../../constants/global';
import { initComponentState, useGlobalContext } from '../../../context/global';
import { useReduxContext } from '../../../context/redux';
import { ballotPartiesQueryOptions } from '../../../hooks/queries/use-ballot-queries';
import { type IUSH, useStateHook } from '../../../hooks/state';
import type { IActions } from '../../../interface/IAction';
import type { IStates } from '../../../interface/IReducer';
import type { IPartyState } from '../../../interface/state/IPartyState';
import type { IComponentState } from '../../../layout/component-state';
import { queryClient } from '../../../lib/query-client';
import type {
	ICreateLGAResult,
	ICreatePUResult,
	ICreateStateResult,
	ICreateWardResult,
	IResult,
} from '../../../store/actions/query/irev';
import type { IrightPanelCTA, IRightSection } from '../../reusable/right-panel/utils';
import type { IChildCode, IPartyVotes, ursType } from './utils';

export const getResults = (states: IStates | undefined, partyVotes: IPartyVotes[]): IResult[] => {
	const allParties = states?._party.get_Party?.data;
	// Flatten and merge duplicates
	const mergedOutput: IResult[] = partyVotes
		? Array.from(
				partyVotes
					?.flatMap?.((item) =>
						item.votes.map((i) => ({
							...i,
							votes: parseInt(`${i.votes}`) || 0,
						}))
					)
					?.reduce((acc, curr) => {
						if (acc.has(curr.partyId)) {
							// Merge votes if partyId already exists

							acc.get(curr.partyId)!.votes += parseInt(`${curr.votes}`);
						} else {
							// Add new entry
							acc.set(curr.partyId, {
								...curr,
								partyName: allParties?.filter((j) => j.id === curr.partyId)?.[0]?.shortName || '',
							});
						}
						return acc;
					}, new Map<number, IResult>())
					.values()
			)
		: [];
	return mergedOutput;
};

const getZoneProps = (states: IStates | undefined) => {
	const zoneProps = states?._geographicalzone?.get_GeographicalZone?.data || [];

	const geoZoneId = states?.state_?.state_getByID?.data?.geographicalZoneId || 0;
	return zoneProps.filter((i) => `${i.id}` === `${geoZoneId}`)?.[0];
};

export const useStateResultDataManager = ({ results: _results }: { results: IResult[] }): { data: ICreateStateResult } => {
	const {
		global: {
			state: { resultSummary, uploadedFiles, selectedElection, selectedParentCode },
		},
	} = useGlobalContext();

	const { states } = useReduxContext();

	const geoZoneProps = getZoneProps(states);

	const data: ICreateStateResult = {
		accreditedVoters: resultSummary.DataAccreditedVoters || 0,
		ballotPapersIssuedToPoolingUnit: resultSummary.DataBallotPapersIssuedToPoolingUnit || 0,
		totalUsedBallotPapers: resultSummary.DataTotalUsedBallotPapers || 0,
		totalValidVotes: resultSummary.DataTotalValidVotes || 0,
		unusedBallotPapers: resultSummary.DataUnusedBallotPapers || 0,
		votersOnRegister: resultSummary.DataVotersOnRegister || 0,
		rejectedBallot: resultSummary.DataRejectedBallot || 0,
		documentUrls: uploadedFiles,
		results: [],
		resultMap: [],
		createdBy: {
			id: parseInt(getUserData().user?.UserId || ''),
			name: getUserData().user?.FullName || '',
		},
		election: {
			id: selectedElection.id,
			name: selectedElection.name,
		},
		localGovernment: {
			code: '',
			name: '',
		},
		presidingOfficer: {
			id: getUserData().user?.UserId || '',
			name: getUserData().user?.FullName || '',
		},
		state: {
			code: selectedParentCode.code,
			name: selectedParentCode.name,
		},
		geoZone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.name,
		},
		zone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.zone?.name,
		},
		flags: [],
	};
	return {
		data,
	};
};

export const useLGAResultDataManager = ({ results: _results }: { results: IResult[] }): { data: ICreateLGAResult } => {
	const {
		global: {
			state: {
				resultSummary,
				uploadedFiles,
				// partyVotes,
				selectedElection,
				selectedParentCode,
			},
		},
	} = useGlobalContext();

	const { states } = useReduxContext();

	const geoZoneProps = getZoneProps(states);
	const data: ICreateLGAResult = {
		accreditedVoters: resultSummary.DataAccreditedVoters || 0,
		ballotPapersIssuedToPoolingUnit: resultSummary.DataBallotPapersIssuedToPoolingUnit || 0,
		totalUsedBallotPapers: resultSummary.DataTotalUsedBallotPapers || 0,
		totalValidVotes: resultSummary.DataTotalValidVotes || 0,
		unusedBallotPapers: resultSummary.DataUnusedBallotPapers || 0,
		votersOnRegister: resultSummary.DataVotersOnRegister || 0,
		rejectedBallot: resultSummary.DataRejectedBallot || 0,
		documentUrls: uploadedFiles,
		results: [],
		resultMap: [],
		createdBy: {
			id: parseInt(getUserData().user?.UserId || ''),
			name: getUserData().user?.FullName || '',
		},
		election: {
			id: selectedElection.id,
			name: selectedElection.name,
		},
		localGovernment: {
			code: selectedParentCode.code,
			name: selectedParentCode.name,
		},
		presidingOfficer: {
			id: getUserData().user?.UserId || '',
			name: getUserData().user?.FullName || '',
		},
		state: {
			code: states?.state_?.state_getByID?.data?.stateCode,
			name: states?.state_?.state_getByID?.data?.name,
		},
		geoZone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.name,
		},
		zone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.zone?.name,
		},
	};
	return {
		data,
	};
};

export const useWardResultDataManager = ({ results: _results }: { results: IResult[] }): { data: ICreateWardResult } => {
	const {
		global: {
			state: {
				// partyVotes,
				uploadedFiles,
				resultSummary,
				selectedElection,
				selectedParentCode,
			},
		},
	} = useGlobalContext();

	const { states } = useReduxContext();

	const geoZoneProps = getZoneProps(states);
	// const getWardResultsMap = () => {
	//   // Flatten and merge duplicates
	//   const transformedArray: IWardResultMap[] = partyVotes.flatMap((item) =>
	//     item.votes.map(({ partyId, votes }) => ({
	//       pollingUnitId: item.id,
	//       partyId,
	//       votes: parseInt(votes + '') || 0
	//     }))
	//   )
	//   return transformedArray
	// }
	const lga = states?.ward_?.ward_getByCode?.data?.lga;
	const data: ICreateWardResult = {
		accreditedVoters: resultSummary.DataAccreditedVoters || 0,
		ballotPapersIssuedToPoolingUnit: resultSummary.DataBallotPapersIssuedToPoolingUnit || 0,
		totalUsedBallotPapers: resultSummary.DataTotalUsedBallotPapers || 0,
		totalValidVotes: resultSummary.DataTotalValidVotes || 0,
		unusedBallotPapers: resultSummary.DataUnusedBallotPapers || 0,
		votersOnRegister: resultSummary.DataVotersOnRegister || 0,
		rejectedBallot: resultSummary.DataRejectedBallot || 0,
		documentUrls: uploadedFiles,
		// results: results.map((i) => ({
		//   partyId: i.partyId,
		//   partyName: i.partyName,
		//   votes: i.votes
		// })),
		results: [],
		// resultMap: getWardResultsMap(),
		resultMap: [],
		createdBy: {
			id: parseInt(getUserData().user?.UserId || ''),
			name: getUserData().user?.FullName || '',
		},
		election: {
			id: selectedElection.id || 0,
			name: selectedElection.name || '',
		},
		ward: {
			code: selectedParentCode.code,
			name: selectedParentCode.name,
		},
		presidingOfficer: {
			id: getUserData().user?.UserId || '',
			name: getUserData().user?.FullName || '',
		},
		state: {
			code: states?.state_?.state_getByID?.data?.stateCode,
			name: states?.state_?.state_getByID?.data?.name,
		},
		geoZone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.name,
		},
		zone: {
			code: geoZoneProps?.code,
			name: geoZoneProps?.zone?.name,
		},
		flags: [],
		localGovernment: {
			code: lga?.lgaCode || '',
			name: lga?.name || '',
		},
	};
	return {
		data,
	};
};

export const usePollingUnitResultDataManager = ({ results: _results }: { results: IResult[] }): { data: ICreatePUResult } => {
	const {
		global: {
			state: { uploadedFiles, resultSummary, selectedElection, selectedParentCode },
		},
	} = useGlobalContext();
	const data: ICreatePUResult = {
		accreditedVoters: resultSummary.DataAccreditedVoters || 0,
		ballotPapersIssuedToPoolingUnit: resultSummary.DataBallotPapersIssuedToPoolingUnit || 0,
		totalUsedBallotPapers: resultSummary.DataTotalUsedBallotPapers || 0,
		totalValidVotes: resultSummary.DataTotalValidVotes || 0,
		unusedBallotPapers: resultSummary.DataUnusedBallotPapers || 0,
		votersOnRegister: resultSummary.DataVotersOnRegister || 0,
		rejectedBallot: resultSummary.DataRejectedBallot || 0,
		documentUrls: uploadedFiles,
		results: [],
		createdBy: {
			id: parseInt(getUserData().user?.UserId || ''),
			name: getUserData().user?.FullName || '',
		},
		election: {
			id: selectedElection.id,
			name: selectedElection.name,
		},
		poolingUnitCode: selectedParentCode.code,
		presidingOfficer: {
			id: getUserData().user?.UserId || '',
			name: getUserData().user?.FullName || '',
		},
		flags: [],
	};
	return {
		data,
	};
};

export interface IUseCodeProps {
	childCodes: IChildCode[];
	clearCodes: () => void;
	handleCodes: () => void;
	load: boolean;
}

export const useGetCodes = ({
	global,
	actions,
	states,
}: {
	global: IUSH<IComponentState>;
	states: IStates | undefined;
	actions: IActions | undefined;
}): IUseCodeProps => {
	const {
		updateState,
		state: { selectedParentCode, childCodes, partyVotes, resultType, electionId, selectedElection },
	} = global;

	// The election being captured, read at fetch time (fetchCodes is memoised on the code alone).
	const electionIdRef = useRef<number | undefined>(undefined);
	electionIdRef.current = Number(electionId) > 0 ? Number(electionId) : Number(selectedElection?.id) || undefined;

	/**
	 * One vote row per party ON THIS ELECTION'S BALLOT. Without an election id, or without a
	 * configured ballot, rows stay one per party as before. Uses the app's shared client
	 * directly: this hook runs in `AllProviders` itself, above its QueryClientProvider.
	 */
	const withBallotParties = (all: IPartyState[], build: (parties: IPartyState[]) => void) => {
		const id = electionIdRef.current;
		if (!id) {
			build(all);
			return;
		}
		queryClient
			.fetchQuery(ballotPartiesQueryOptions(id))
			.then((ballot) => build(scopePartiesToBallot(all, ballot)))
			.catch(() => build(all));
	};

	const fetchCodes = useCallback(() => {
		const id = [selectedParentCode.codeId];
		if (resultType === 'EC8A') {
			actions?.get_PoolingUnitByCode({
				poolingUnitCode: selectedParentCode.code,
				onSuccess: (res) => {
					actions?.lga_getById({
						id: `${res.data?.ward?.lgaId}`,
					});
					actions?.get_Party({
						onSuccess: (partyRes) =>
							withBallotParties(partyRes.data, (ballotParties) => {
								if (!partyVotes.length || partyVotes.length !== ballotParties.length) {
									updateState('partyVotes', [
										{
											id: selectedParentCode.codeId,
											votes: ballotParties.map((vote) => ({
												partyId: vote.id,
												votes: 0,
												label: vote.shortName,
												logo: vote.logo,
											})),
										},
									]);
								}
							}),
					});
				},
			});
		}
		if (resultType === 'EC8B') {
			actions?.get_PoolingUnitInWard({
				data: {
					wardIds: id,
				},
				onSuccess: (res) => {
					if (!childCodes.length || childCodes.length !== res.data.length) {
						updateState(
							'childCodes',
							res.data.map((i) => ({
								code: i.poolingUnitCode,
								codeId: i.id,
								parent: i.wardId,
								name: i.name,
								status: false,
							}))
						);
						actions?.get_Party({
							onSuccess: (parties) =>
								withBallotParties(parties.data, (ballotParties) => {
									if (!partyVotes.length || partyVotes.length !== ballotParties.length) {
										updateState(
											'partyVotes',
											res.data.map((i) => ({
												id: i.id,
												votes: ballotParties.map((vote) => ({
													partyId: vote.id,
													votes: 0,
													label: vote.shortName,
													logo: vote.logo,
												})),
											}))
										);
									}
								}),
						});
					}
				},
			});
		}
		if (resultType === 'EC8C') {
			actions?.ward_getInLGA({
				data: {
					lgaIds: id,
				},
				onSuccess: (res) => {
					if (!childCodes.length || childCodes.length !== res.data.length) {
						updateState(
							'childCodes',
							res.data.map((i) => ({
								code: i.wardCode,
								name: i.name,
								codeId: i.id,
								parent: i.lgaId,
								status: false,
							}))
						);
						actions?.get_Party({
							onSuccess: (parties) =>
								withBallotParties(parties.data, (ballotParties) => {
									if (!partyVotes.length || partyVotes.length !== ballotParties.length) {
										updateState(
											'partyVotes',
											res.data.map((i) => ({
												id: i.id,
												votes: ballotParties.map((vote) => ({
													partyId: vote.id,
													votes: 0,
													label: vote.shortName,
													logo: vote.logo,
												})),
											}))
										);
									}
								}),
						});
					}
				},
			});
		}
		if (resultType === 'EC8D') {
			actions?.lga_getInState({
				data: {
					stateIds: id,
				},
				onSuccess: (res) => {
					if (!childCodes.length || childCodes.length !== res.data.length) {
						updateState(
							'childCodes',
							res.data.map((i) => ({
								code: i.lgaCode,
								codeId: i.id,
								parent: i.stateId,
								name: i.name,
								status: false,
							}))
						);
						actions?.get_Party({
							onSuccess: (parties) =>
								withBallotParties(parties.data, (ballotParties) => {
									if (!partyVotes.length || partyVotes.length !== ballotParties.length) {
										updateState(
											'partyVotes',
											res.data.map((i) => ({
												id: i.id,
												votes: ballotParties.map((vote) => ({
													partyId: vote.id,
													votes: 0,
													label: vote.shortName,
													logo: vote.logo,
												})),
											}))
										);
									}
								}),
						});
					}
				},
			});
		}
	}, [resultType, selectedParentCode.codeId]);

	const clearCodes = () => {
		updateState('parentCodes', []);
		updateState('childCodes', []);
	};

	return {
		childCodes,
		clearCodes,
		handleCodes: fetchCodes,
		load:
			states?._poolingUnit?.get_PoolingUnitByCodeLoading ||
			states?.ward_?.ward_getByCodeLoading ||
			states?._poolingUnit?.get_PoolingUnitInWardLoading ||
			states?.ward_?.ward_getInLGALoading ||
			states?._party?.get_PartyLoading ||
			states?._party?.get_PartyLoading ||
			states?.lga_?.lga_getInStateLoading ||
			false,
	};
};

export const useAppData = (): {
	global: IUSH<IComponentState>;
} => {
	const global = useStateHook<IComponentState>(initComponentState);

	return {
		global,
	};
};

interface IUseGetMetaDataProps {
	getMetaDataInfo: (
		props: {
			name?: string;
			id?: number;
			code?: string;
		},
		resultType: ResultType
	) => void;
	metaData: string;
}

export const useGetMetaDataInfo = (): IUseGetMetaDataProps => {
	const { actions } = useReduxContext();

	const [metaData, setMetaData] = useState<string>('');
	const getMetaDataInfo = (props: { name?: string; id?: number; code?: string }, resultType: ResultType) => {
		setMetaData('');
		const id = props.id ? `${props.id}` : '';
		if (resultType === 'EC8A') {
			actions?.get_PoolingUnitByCode({
				poolingUnitCode: props.code || '',
				onSuccess: (res) => {
					setMetaData(res.data.description);
				},
			});
		}
		if (resultType === 'EC8B') {
			actions?.ward_getByID({
				id,
				onSuccess: (res) => {
					setMetaData(res.data.description);
				},
			});
		}
		if (resultType === 'EC8C') {
			actions?.lga_getById({
				id,
				onSuccess: (res) => {
					setMetaData(res.data.description);
				},
			});
		}
		if (resultType === 'EC8D') {
			actions?.state_getByID({
				id,
				onSuccess: (_res) => {
					setMetaData('');
				},
			});
		}
	};
	return {
		getMetaDataInfo,
		metaData,
	};
};

export interface IUseExtraceDataAI {
	extractData: () => void;
	prompt: string;
	navIndex: number;
	setNavIndex: React.Dispatch<React.SetStateAction<number>>;
	isExtract: boolean;
	load: boolean | undefined;
	setIsExtract: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useExtractDataAI = (): IUseExtraceDataAI => {
	const {
		global: { state },
	} = useGlobalContext();

	const { actions, states } = useReduxContext();

	const [isExtract, setIsExtract] = useState<boolean>(false);
	const [navIndex, setNavIndex] = useState<number>(0);
	const load = states?._result?.extract_ResultFileLoading;
	const isSuccessful = !!states?._result?.extract_ResultFile?.documentTitle;
	const prompt = load ? 'Extracting Results...' : isSuccessful ? 'Extraction Successful' : 'Extraction Failed';

	const extractData = () => {
		const file = state.files[navIndex];
		const formData = new FormData();
		formData.append('file', file);
		actions?.extract_ResultFile({
			data: formData,
			onSuccess: () => {},
		});
		// actions?.extract_ResultFile({
		//   url: state.uploadedFiles[navIndex],
		//   onSuccess: () => {}
		// })
		setIsExtract(true);
	};

	return {
		extractData,
		prompt,
		navIndex,
		setNavIndex,
		isExtract,
		setIsExtract,
		load,
	};
};

export const useSelectElectionCTA = (
	rightPanel: IRightSection<{}> | undefined,
	onStage: () => void,
	isElectionId: boolean,
	stage: ursType
) => {
	const selectElectionCTA: IrightPanelCTA[] = [
		{
			title: 'Next >> Select Result Type',
			buttonType: 'bold',
			action: onStage,
		},
	];

	useEffect(() => {
		if (isElectionId && stage === 'Select Election') {
			rightPanel?.setCTA(selectElectionCTA);
		}
		return () => {
			rightPanel?.setCTA([]);
		};
	}, [isElectionId, stage]);
};

export const useResultTypeCTA = (
	rightPanel: IRightSection<{}> | undefined,
	onPrevious: () => void,
	onNext: () => void,
	stage: ursType
) => {
	const cta: IrightPanelCTA[] = [
		{
			title: 'Previous',
			buttonType: 'outlined',
			action: onPrevious,
		},
		{
			title: 'Next',
			buttonType: 'bold',
			action: onNext,
		},
	];

	useEffect(() => {
		if (stage === 'Select Result Type') {
			rightPanel?.setCTA(cta);
		}
		return () => {
			rightPanel?.setCTA([]);
		};
	}, [stage]);
};

export const useElectoralDivisionCTA = (
	rightPanel: IRightSection<{}> | undefined,
	onPrevious: () => void,
	onStage: () => void,
	isParentCode: boolean,
	stage: ursType
) => {
	const selectElectionCTA: IrightPanelCTA[] = [
		{
			title: 'Previous',
			buttonType: 'outlined',
			action: onPrevious,
		},
		{
			title: 'Next >> Upload Result',
			buttonType: 'bold',
			action: onStage,
		},
	];

	useEffect(() => {
		if (isParentCode && stage === 'Select Constituent Metadata') {
			rightPanel?.setCTA(selectElectionCTA);
		}
		return () => {
			rightPanel?.setCTA([]);
		};
	}, [isParentCode, stage]);
};

export const useUploadResultCTA = (
	rightPanel: IRightSection<{}> | undefined,
	endPreview: () => void,
	stage: ursType,
	isExcel: boolean,
	handleExcelFileSelect: () => void,
	isUpdate: boolean,
	onUploadDocument: () => void,
	isFile: boolean,
	isUploaded: boolean,
	preview: boolean,
	isSubmitExcel: boolean,
	submitExcelResult: () => void,
	cancelExcelResultUpload: () => void,
	onPrevious: () => void
) => {
	const cta: IrightPanelCTA[] = useMemo(() => {
		const previousCTA: IrightPanelCTA[] = [
			{
				title: 'Previous',
				buttonType: 'outlined',
				action: onPrevious,
			},
		];

		const excelCTA: IrightPanelCTA[] = isExcel
			? [
					{
						title: 'Upload Excel Result for EC8-A',
						buttonType: 'outlined',
						action: handleExcelFileSelect,
					},
				]
			: [];
		const uploadCTA: IrightPanelCTA[] =
			isFile && !isUploaded
				? [
						{
							title: !isUpdate ? 'Upload' : 'Update',
							buttonType: 'bold',
							action: onUploadDocument,
						},
					]
				: [];
		const resultSummaryCTA: IrightPanelCTA[] =
			(isUploaded || preview) && isFile
				? [
						{
							title: 'End Preview',
							buttonType: 'bold',
							action: endPreview,
						},
					]
				: [];

		const submitExcelCTA: IrightPanelCTA[] = isSubmitExcel
			? [
					{
						title: 'Submit Result',
						buttonType: 'bold',
						action: submitExcelResult,
					},
					{
						title: 'Cancel',
						buttonType: 'danger',
						action: cancelExcelResultUpload,
					},
				]
			: [];
		return [...previousCTA, ...excelCTA, ...uploadCTA, ...resultSummaryCTA, ...submitExcelCTA];
	}, [isExcel, isFile, isUploaded, preview, isSubmitExcel]);

	useEffect(() => {
		if (stage === 'Upload Result') {
			rightPanel?.setCTA(cta);
		}
		return () => {
			rightPanel?.setCTA([]);
		};
	}, [stage, cta]);
};

export const usePreviewMediaCTA = (rightPanel: IRightSection<{}> | undefined, onDone: () => void, stage: ursType) => {
	const previewCTA: IrightPanelCTA[] = [
		{
			title: 'Done',
			buttonType: 'bold',
			action: onDone,
		},
	];

	useEffect(() => {
		if (stage === 'Preview Uploaded File') {
			rightPanel?.setCTA(previewCTA);
		}
		return () => {
			rightPanel?.setCTA([]);
		};
	}, [stage]);
};

export const useUploadResultStatusCTA = (
	rightPanel: IRightSection<{}> | undefined,
	onView: () => void,
	// onNext: () => void,
	stage: ursType
) => {
	const cta: IrightPanelCTA[] = [
		{
			title: 'Preview',
			buttonType: 'bold',
			action: onView,
		},
		// {
		//   title: 'Next >> Election Result Summary',
		//   buttonType: 'bold',
		//   action: onNext
		// }
	];

	useEffect(() => {
		if (stage === 'Upload Result Status') {
			rightPanel?.setCTA(cta);
		}
		return () => {
			rightPanel?.setCTA([]);
		};
	}, [stage]);
};

export const useAddResultSummaryCTA = (
	rightPanel: IRightSection<{}> | undefined,
	_onCompleted: () => void,
	_onPreview: () => void,
	stage: ursType,
	preview: boolean,
	_onPrevious: () => void
) => {
	// const cta: IrightPanelCTA[] = [
	//   {
	//     title: 'Previous',
	//     buttonType: 'outlined',
	//     action: onPrevious
	//   },
	//   {
	//     title: 'Next >> Party Vote Count',
	//     buttonType: 'bold',
	//     action: onCompleted
	//   }
	// ]
	// const previewCta: IrightPanelCTA[] = preview
	//   ? [
	//       {
	//         title: 'End Preview',
	//         buttonType: 'outlined',
	//         action: onPreview
	//       }
	//     ]
	//   : []

	useEffect(
		() =>
			// if (stage === 'Add Result Summary') {
			//   rightPanel?.setCTA([...cta, ...previewCta])
			// }
			() => {
				rightPanel?.setCTA([]);
			},
		[stage, preview]
	);
};

export const usePartyVotesCTA = (
	rightPanel: IRightSection<{}> | undefined,
	_isCode: boolean,
	_onPartyVoteCompleted: () => void,
	stage: ursType,
	isPartyVotes: boolean,
	preview: boolean,
	_endPreview: () => void
) => {
	// const cta: IrightPanelCTA[] = isPartyVotes
	//   ? [
	//       {
	//         title: isCode ? 'Save Votes' : 'Next >> Preview',
	//         buttonType: 'bold',
	//         action: onPartyVoteCompleted
	//       }
	//     ]
	//   : []

	// const previewCTA: IrightPanelCTA[] = preview
	//   ? [
	//       {
	//         title: 'End Preview',
	//         buttonType: 'outlined',
	//         action: endPreview
	//       }
	//     ]
	//   : []

	useEffect(
		() =>
			// if (stage === 'Add Party Vote Count') {
			//   rightPanel?.setCTA([...cta, ...previewCTA])
			// }
			() => {
				rightPanel?.setCTA([]);
			},
		[stage, isPartyVotes, preview]
	);
};

export const usePreviewCTA = (rightPanel: IRightSection<{}> | undefined, onSubmit: () => void, stage: ursType, load: boolean) => {
	const cta: IrightPanelCTA[] = [
		{
			title: 'Submit Result',
			buttonType: 'bold',
			action: onSubmit,
			load,
			loadText: 'Submitting Election Result',
		},
	];

	useEffect(() => {
		if (stage === 'Preview') {
			rightPanel?.setCTA(cta);
		}
		return () => {
			rightPanel?.setCTA([]);
		};
	}, [stage, load]);
};

export const useVoteSummaryCTA = (rightPanel: IRightSection<{}> | undefined, _onSubmit: () => void, stage: ursType) => {
	// const cta: IrightPanelCTA[] = [
	//   {
	//     title: 'Done',
	//     buttonType: 'bold',
	//     action: onSubmit
	//   }
	// ]

	useEffect(
		() =>
			// if (stage === 'Party Vote Summary') {
			//   rightPanel?.setCTA(cta)
			// }
			() => {
				rightPanel?.setCTA([]);
			},
		[stage]
	);
};

export const usePartyVotesStageCTA = (
	rightPanel: IRightSection<{}> | undefined,
	_onPrevious: () => void,
	_onDone: () => void,
	_onEndPreview: () => void,
	stage: ursType,
	preview: boolean,
	isDone: boolean
) => {
	// const cta: IrightPanelCTA[] = [
	//   {
	//     title: 'Previous',
	//     buttonType: 'outlined',
	//     action: onPrevious
	//   }
	// ]

	// const nextCta: IrightPanelCTA[] =
	//   !preview || isDone
	//     ? [
	//         {
	//           title: 'Next >> Preview',
	//           buttonType: 'bold',
	//           action: onDone
	//         }
	//       ]
	//     : []
	// const previewCta: IrightPanelCTA[] =
	//   preview && isDone
	//     ? [
	//         {
	//           title: 'End Preview',
	//           buttonType: 'bold',
	//           action: onEndPreview
	//         }
	//       ]
	//     : []

	useEffect(
		() =>
			// if (stage === 'Add Party Vote Count') {
			//   rightPanel?.setCTA([...cta, ...nextCta, ...previewCta])
			// }
			() => {
				rightPanel?.setCTA([]);
			},
		[stage, preview, isDone]
	);
};
